import { FC, useEffect, useState } from "react";
import { Button, Modal, Text } from "rizzui";
import { toast } from "sonner";
import { WorkHourService } from "../../../services/workHour/workHourService";
import { useAtom } from "jotai";
import { formsSubmitAtom } from "../../../atom/atom";
import { MdOutlineCloudUpload } from "react-icons/md";
import { HiOutlineDownload } from "react-icons/hi";
import useHandleErrors from "../../../hooks/useHandleErrors";

interface WorkHoursImportModalProps {
    isOpen: boolean;
    onClose: () => void;
    refetch: () => void;
}

type ImportSummary = {
    message: string;
    skipped: string[];
};

const WorkHoursImportModal: FC<WorkHoursImportModalProps> = ({ isOpen, onClose, refetch }) => {

    const [file, setFile] = useState<File | null>(null);
    const [importSummary, setImportSummary] = useState<ImportSummary | null>(null);
    const service = new WorkHourService();
    const [isSubmitting, setIsSubmitting] = useAtom(formsSubmitAtom);
    const { handleErrors } = useHandleErrors();

    useEffect(() => {
        if (isOpen) {
            setImportSummary(null);
        }
    }, [isOpen]);

    const handleClose = () => {
        setImportSummary(null);
        setFile(null);
        onClose();
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            setFile(e.target.files[0]);
        }
    };

    const handleSubmit = async () => {
        if (!file) {
            toast.error("Por favor selecciona un archivo");
            return;
        }

        setIsSubmitting(true);
        const formData = new FormData();
        formData.append("file", file);

        try {
            const response = await (await service.importExcel(formData)).getResponseData();
            if (response.success) {
                const skipped: string[] = Array.isArray(response.data?.skipped_rows)
                    ? response.data.skipped_rows
                    : [];
                setImportSummary({
                    message: response.message ?? "Importación completada",
                    skipped,
                });
                refetch();
                toast.success(
                    skipped.length > 0
                        ? "Importación completada. El detalle de filas omitidas está en esta ventana."
                        : "Importación completada.",
                    { duration: 4000 }
                );
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const copySkippedList = async () => {
        if (!importSummary?.skipped.length) return;
        const text = ["Filas ignoradas:", ...importSummary.skipped.map((line) => `• ${line}`)].join("\n");
        try {
            await navigator.clipboard.writeText(text);
            toast.success("Listado copiado al portapapeles");
        } catch {
            toast.error("No se pudo copiar al portapapeles");
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={handleClose}>
            <div className="p-6">
                {importSummary ? (
                    <>
                        <Text className="text-xl font-semibold text-green-700 dark:text-green-400">
                            Resultado de la importación
                        </Text>
                        <Text className="text-gray-700 dark:text-gray-300 mt-3 whitespace-pre-line">
                            {importSummary.message}
                        </Text>
                        <div className="mt-5">
                            <Text className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-2">
                                Filas ignoradas (no importadas) y motivo
                            </Text>
                            {importSummary.skipped.length === 0 ? (
                                <Text className="text-sm text-gray-500">No hubo filas omitidas.</Text>
                            ) : (
                                <>
                                    <pre className="max-h-72 overflow-y-auto rounded-md border border-gray-200 bg-gray-50 p-3 text-left text-sm text-gray-800 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 whitespace-pre-wrap font-sans">
                                        {importSummary.skipped.map((line) => `• ${line}`).join("\n")}
                                    </pre>
                                    <Button variant="outline" size="sm" className="mt-3" onClick={copySkippedList}>
                                        Copiar listado
                                    </Button>
                                </>
                            )}
                        </div>
                        <div className="mt-6 flex justify-end">
                            <Button onClick={handleClose}>Cerrar</Button>
                        </div>
                    </>
                ) : (
                    <>
                        <div className="mb-6">
                            <Text className="text-xl font-semibold">Importar Horas Trabajadas desde Excel</Text>
                            <Text className="text-gray-500 mt-2">
                                Selecciona un archivo Excel (.xlsx, .xls) para importar horas trabajadas.
                                Asegúrate de que el formato sea correcto.
                            </Text>
                            <div className="mt-4">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    className="flex items-center gap-2"
                                    onClick={() => {
                                        const url = `${process.env.REACT_APP_API_PUBLIC_URL}/excel-template/work-hour`;
                                        window.open(url, "_blank");
                                    }}
                                >
                                    <HiOutlineDownload className="w-4 h-4" />
                                    Descargar Plantilla Excel
                                </Button>
                            </div>
                        </div>

                        <div className="mb-6">
                            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-primary transition-colors cursor-pointer relative">
                                <input
                                    type="file"
                                    accept=".xlsx, .xls"
                                    onChange={handleFileChange}
                                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                />
                                <div className="flex flex-col items-center justify-center pointer-events-none">
                                    <MdOutlineCloudUpload className="w-12 h-12 text-gray-400 mb-2" />
                                    <Text className="text-gray-600 font-medium">
                                        {file ? file.name : "Haz clic o arrastra un archivo aquí"}
                                    </Text>
                                    <Text className="text-xs text-gray-400 mt-1">
                                        Soporta .xlsx, .xls
                                    </Text>
                                </div>
                            </div>
                        </div>

                        <div className="flex justify-end gap-3">
                            <Button variant="outline" onClick={handleClose} disabled={isSubmitting}>
                                Cancelar
                            </Button>
                            <Button
                                onClick={handleSubmit}
                                isLoading={isSubmitting}
                                disabled={!file}
                            >
                                Importar
                            </Button>
                        </div>
                    </>
                )}
            </div>
        </Modal>
    );
};

export default WorkHoursImportModal;
